import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-relay';
import { Link, useLocation } from 'react-router-dom';
import { graphql } from 'relay-runtime';
import Text from '../../tenant_portal/fields/Text';
import type { FieldsDeepUpdate } from '../../typescript/types/FieldComponentProps';
import { FIELD_MODES, updateDeep } from '../../components/fields/FieldTools';
import type { ErrorType } from '../../components/Types';
import {
  camelCaseToTitle,
  filterErrorsByPath,
  formatErrorMessages,
} from '../../components/Tools';
import type { EmailLoginCreateAccountMutation$data } from './__generated__/EmailLoginCreateAccountMutation.graphql';
import PortalButton from '../../components/PortalButton';
import SubmitSpinner from '../../components/SubmitSpinner';
import PageHeader from '../../tenant_portal/shared/PageHeader';
import LoginBackButtonPortal from '../LoginBackButtonPortal';
import {
  FREE_TRIAL_SIGNUP_TYPE,
  getIntentLoginPathForPathname,
  getSignupTypeForPathname,
} from '../RouteIntents';
import loginStyles from '../../../../stylesheets/components/login.module.scss';
import SomethingWentWrongAlert from '../SomethingWentWrongAlert';

export const LoginCreateAccountMutation = graphql`
  mutation EmailLoginCreateAccountMutation($input: LoginCreateAccountInput!) {
    loginCreateAccount(input: $input) {
      errors {
        path
        messages
      }
    }
  }
`;

const initialData = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirmation: '',
};

const getPrefilledFields = ({
  email,
  firstName,
  lastName,
}: {
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
}) => {
  return {
    ...initialData,
    firstName: firstName || '',
    lastName: lastName || '',
    email: email || '',
  };
};
interface EmailProps {
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
}

const Email = ({ email, firstName, lastName }: EmailProps) => {
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const [fields, setFields] = useState(() =>
    getPrefilledFields({
      email,
      firstName,
      lastName,
    })
  );
  const [errors, setErrors] = useState<ErrorType[]>([]);
  const [commitMutation, isMutationInFlight] = useMutation(
    LoginCreateAccountMutation
  );
  const signupType = getSignupTypeForPathname(location.pathname);
  const isFreeTrialSignup = signupType === FREE_TRIAL_SIGNUP_TYPE;
  const signInPath = getIntentLoginPathForPathname(location.pathname);

  const generalErrors = filterErrorsByPath(
    errors,
    ['firstName', 'lastName', 'email', 'password', 'passwordConfirmation'],
    false
  );
  let formattedGeneralErrorMessages = '';
  generalErrors.forEach((error) => {
    const relPath = error.path.split('.').slice(-1)[0];
    const label = relPath === 'base' ? '' : camelCaseToTitle(relPath);
    formattedGeneralErrorMessages += formatErrorMessages([error], label);
  });
  const somethingWentWrongAlert = (
    <SomethingWentWrongAlert
      body={formattedGeneralErrorMessages}
      resetKey={errors}
    />
  );
  const passwordErrors = filterErrorsByPath(errors, ['password']);

  const handleFieldsChange: FieldsDeepUpdate = (fieldName, value) => {
    setFields((prevFields) => {
      return updateDeep(prevFields, fieldName, value);
    });
  };

  const validateFields = () => {
    const validationErrors: ErrorType[] = [];
    Object.entries(fields).forEach(([key, value]) => {
      if (value === '') {
        validationErrors.push({
          path: key,
          messages: ["can't be blank"],
        });
      }
    });

    if (fields.password && fields.password.length < 6) {
      validationErrors.push({
        path: 'password',
        messages: ['is too short (minimum is 6 characters)'],
      });
    }

    if (
      fields.password &&
      fields.passwordConfirmation &&
      fields.password !== fields.passwordConfirmation
    ) {
      validationErrors.push({
        path: 'passwordConfirmation',
        messages: ['does not match password'],
      });
    }

    return validationErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    commitMutation({
      variables: {
        input: {
          login: {
            firstName: fields.firstName,
            lastName: fields.lastName,
            email: fields.email,
            password: fields.password,
            passwordConfirmation: fields.passwordConfirmation,
            signupType,
            ...(isFreeTrialSignup
              ? {}
              : {
                  encryptedEmail:
                    new URLSearchParams(location.search).get('email') || '',
                }),
          },
        },
      },
      onCompleted: (response) => {
        const responseData = (response as EmailLoginCreateAccountMutation$data)
          ?.loginCreateAccount;
        if (!responseData) {
          setErrors([
            {
              path: 'base',
              messages: [
                'There was an error creating your account. Please contact Property Matrix support for assistance.',
              ],
            },
          ]);
          setRedirecting(false);
          return;
        }
        if (responseData.errors && responseData.errors.length > 0) {
          setErrors(responseData.errors as ErrorType[]);
          setRedirecting(false);
          return;
        }
        if (isFreeTrialSignup) {
          window.location.href = '/properties/free-trial';
          return;
        }
        // If user is logged in and goes to login page, it uses LoginsController.redirect_if_logged_in to redirect them to the right portal
        window.location.href = window.RAILS_CONSTANTS['login_path'];
      },
      onError: () => {
        setErrors([
          {
            path: 'base',
            messages: [
              'There was an error creating your account. Please contact Property Matrix support for assistance.',
            ],
          },
        ]);
        setRedirecting(false);
      },
    });
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  useEffect(() => {
    if (errors.length > 0) {
      handleFieldsChange('password', '');
      handleFieldsChange('passwordConfirmation', '');
    }
  }, [errors]);

  useEffect(() => {
    setFields((prevFields) => {
      const nextFields = getPrefilledFields({
        email,
        firstName,
        lastName,
      });

      if (
        prevFields.firstName === nextFields.firstName &&
        prevFields.lastName === nextFields.lastName &&
        prevFields.email === nextFields.email
      ) {
        return prevFields;
      }

      return {
        ...prevFields,
        firstName: nextFields.firstName,
        lastName: nextFields.lastName,
        email: nextFields.email,
      };
    });
  }, [email, firstName, lastName]);

  if (isMutationInFlight || redirecting) {
    if (!redirecting) setRedirecting(true);
    return <SubmitSpinner spinnerText={'Creating account'} />;
  }

  return (
    <div className={loginStyles.loginRouteContent}>
      <LoginBackButtonPortal to={`../${location.search}`} />
      <PageHeader title="Create an account" includeBottomMargin={false} />
      <p className={loginStyles.loginSubtitle}>
        Already have an account?{' '}
        <Link to={signInPath} className={loginStyles.loginActionLink}>
          Sign in
        </Link>
      </p>
      {somethingWentWrongAlert}
      <form className={loginStyles.loginForm} onSubmit={handleFormSubmit}>
        <Text
          field={{
            label: 'First name',
            name: 'firstName',
            value: fields.firstName,
            autocomplete: 'given-name',
            autoFocus: true,
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleFieldsChange}
          errors={filterErrorsByPath(errors, ['firstName'])}
        />
        <Text
          field={{
            label: 'Last name',
            name: 'lastName',
            value: fields.lastName,
            autocomplete: 'family-name',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleFieldsChange}
          errors={filterErrorsByPath(errors, ['lastName'])}
        />
        <Text
          field={{
            label: 'Email',
            name: 'email',
            type: 'email',
            value: fields.email,
            autocomplete: 'username',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleFieldsChange}
          errors={filterErrorsByPath(errors, ['email'])}
        />
        <Text
          field={{
            label: 'Password',
            name: 'password',
            value: fields.password,
            type: 'password',
            autocomplete: 'new-password',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleFieldsChange}
          errors={passwordErrors}
        />
        {passwordErrors.length === 0 && (
          <p className={loginStyles.loginPasswordHelper}>
            Use at least 6 characters.
          </p>
        )}
        <Text
          field={{
            label: 'Confirm password',
            name: 'passwordConfirmation',
            value: fields.passwordConfirmation,
            type: 'password',
            autocomplete: 'new-password',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleFieldsChange}
          errors={filterErrorsByPath(errors, ['passwordConfirmation'])}
        />
        <button type="submit" hidden /> {/* Allows submit on enter */}
      </form>
      <PortalButton
        id="create-account-button"
        onClick={handleSubmit}
        extraClasses={[loginStyles.loginPrimaryButton]}
      >
        Create account
      </PortalButton>
    </div>
  );
};
export default Email;
